import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageBar from '../../components/PageBar';
import Icon from '../../components/common/Icon';
import ToggleSwitch from '../../components/common/ToggleSwitch';
import SelectSetting from '../../components/common/SelectSetting';
import ColorThemePreview from '../../components/common/ColorThemePreview';
import theme from '../../utils/theme';
import { setDocTitle } from '../../utils/utilities';
import { settingKeys } from '../../utils/settings';
import { COLOR_THEMES, COLOR_THEME_NONE } from '../../helpers/colorThemes';
import { applyColorTheme } from '../../utils/colorTheme';
import { LANGUAGES } from '../../i18n';
import { changeLanguage } from '../../helpers/accountLanguage';
import useBooleanSetting from '../../hooks/useBooleanSetting';
import StorageSection from './components/StorageSection';
import useSetting from '../../hooks/useSetting';
import { ReactComponent as LifebeltIcon } from '../../icons/lifebelt.svg';
import packageJson from '../../../package.json';
import './Settings.scss';

function Settings() {
  const { t, i18n } = useTranslation();
  const [darkTheme, setDarkTheme] = useState(false);
  const [colorTheme, setColorTheme] = useSetting(settingKeys.colorTheme, COLOR_THEME_NONE);
  const [preferSharps, togglePreferSharps] = useBooleanSetting(settingKeys.preferSharps, true);
  const [showDuplicateIcon, toggleShowDuplicateIcon] = useBooleanSetting(settingKeys.showDuplicateIcon);
  const [showMidiIcon, toggleShowMidiIcon] = useBooleanSetting(settingKeys.showMidiIcon, true);
  const [showChordOnTap, toggleShowChordOnTap] = useBooleanSetting(settingKeys.showChordOnTap);
  const [superScroll, toggleSuperScroll] = useBooleanSetting(settingKeys.superScroll);
  const [superMetronome, toggleSuperMetronome] = useBooleanSetting(settingKeys.superMetronome);
  const [superscriptQualifiers, toggleSuperscriptQualifiers] = useBooleanSetting(settingKeys.superscriptQualifiers);
  const [chordSymbols, toggleChordSymbols] = useBooleanSetting(settingKeys.chordSymbols);
  const [colorBorrowedChords, toggleColorBorrowedChords] = useBooleanSetting(settingKeys.colorBorrowedChords);
  const [repeatCountAsChip, toggleRepeatCountAsChip] = useBooleanSetting(settingKeys.repeatCountAsChip);

  useEffect(() => {
    setThemeToState();
  }, []);

  useEffect(() => {
    setDocTitle(t('settings.title'));
  }, [t]);

  const setThemeToState = () => {
    if (theme.isDarkTheme()) {
      setDarkTheme(true);
    }
  };

  const handleDarkThemeToggle = () => {
    if (!darkTheme) theme.setDarkTheme();
    else theme.removeDarkTheme();

    setDarkTheme(!darkTheme);
  };

  const colorThemeOptions = COLOR_THEMES.map(({ id }) => ({ id, name: t(`colorThemes.${id}`) }));

  const handleColorThemeChange = (id) => {
    setColorTheme(id);
    applyColorTheme(id);
  };

  return (
    <div className="mg-container">
      <PageBar back={{ label: t('common.back'), icon: true }} title={t('settings.title')} />
      <div className="Settings">
        <div className="settings-section">
          <h6 className="settings-section-title">{t('settings.language.section')}</h6>
          <SelectSetting
            name="language"
            label={t('settings.language.label')}
            value={i18n.language}
            options={LANGUAGES}
            onChange={changeLanguage}
          />
        </div>

        <div className="settings-section">
          <h6 className="settings-section-title">{t('settings.appearance.section')}</h6>
          <ToggleSwitch
            name="darkTheme"
            value={darkTheme}
            checked={darkTheme}
            label={t('settings.appearance.darkMode')}
            onChange={handleDarkThemeToggle}
            showOnOffText={true}
          />
          <SelectSetting
            name="colorTheme"
            label={t('settings.appearance.colorTheme')}
            value={colorTheme}
            options={colorThemeOptions}
            onChange={handleColorThemeChange}
          >
            <ColorThemePreview themeId={colorTheme} />
          </SelectSetting>
        </div>

        <div className="settings-section">
          <h6 className="settings-section-title">{t('settings.songView.section')}</h6>
          <ToggleSwitch
            name="showDuplicateIcon"
            value={showDuplicateIcon}
            checked={showDuplicateIcon}
            label={t('settings.songView.duplicateIcon')}
            description={t('settings.songView.duplicateIconDescription')}
            onChange={toggleShowDuplicateIcon}
            showOnOffText={true}
          />
          <ToggleSwitch
            name="showMidiIcon"
            value={showMidiIcon}
            checked={showMidiIcon}
            label={t('settings.songView.melodyIcon')}
            description={t('settings.songView.melodyIconDescription')}
            onChange={toggleShowMidiIcon}
            showOnOffText={true}
          />
          <ToggleSwitch
            name="showChordOnTap"
            value={showChordOnTap}
            checked={showChordOnTap}
            label={t('settings.songView.chordPiano')}
            description={t('settings.songView.chordPianoDescription')}
            onChange={toggleShowChordOnTap}
            showOnOffText={true}
          />
          <ToggleSwitch
            name="superScroll"
            value={superScroll}
            checked={superScroll}
            label={t('settings.songView.superScroll')}
            description={t('settings.songView.superScrollDescription')}
            onChange={toggleSuperScroll}
            showOnOffText={true}
          />
          <ToggleSwitch
            name="superMetronome"
            value={superMetronome}
            checked={superMetronome}
            label={t('settings.songView.superMetronome')}
            description={t('settings.songView.superMetronomeDescription')}
            onChange={toggleSuperMetronome}
            showOnOffText={true}
          />
          <ToggleSwitch
            name="repeatCountAsChip"
            value={repeatCountAsChip}
            checked={repeatCountAsChip}
            label={t('settings.songView.repeatCountAsChip')}
            description={t('settings.songView.repeatCountAsChipDescription')}
            onChange={toggleRepeatCountAsChip}
            showOnOffText={true}
          />
        </div>

        <div className="settings-section">
          <h6 className="settings-section-title">{t('settings.chords.section')}</h6>
          <ToggleSwitch
            name="preferSharps"
            value={preferSharps}
            checked={preferSharps}
            label={t('settings.chords.preferSharps')}
            description={t('settings.chords.preferSharpsDescription')}
            onChange={togglePreferSharps}
            showOnOffText={true}
          />
          <ToggleSwitch
            name="superscriptQualifiers"
            value={superscriptQualifiers}
            checked={superscriptQualifiers}
            label={t('settings.chords.superscriptQualifiers')}
            description={t('settings.chords.superscriptQualifiersDescription')}
            onChange={toggleSuperscriptQualifiers}
            showOnOffText={true}
          />
          <ToggleSwitch
            name="chordSymbols"
            value={chordSymbols}
            checked={chordSymbols}
            label={t('settings.chords.chordSymbols')}
            description={t('settings.chords.chordSymbolsDescription')}
            onChange={toggleChordSymbols}
            showOnOffText={true}
          />
          <ToggleSwitch
            name="colorBorrowedChords"
            value={colorBorrowedChords}
            checked={colorBorrowedChords}
            label={t('settings.chords.colorBorrowedChords')}
            description={t('settings.chords.colorBorrowedChordsDescription')}
            onChange={toggleColorBorrowedChords}
            showOnOffText={true}
          />
        </div>

        <StorageSection />

        <div className="settings-section">
          <h6 className="settings-section-title">{t('settings.help.section')}</h6>
          <Link className="contact-row" to="/contact">
            <Icon icon={<LifebeltIcon />} />
            <span className="contact-row-text">
              <span className="contact-row-label">{t('settings.help.contactUs')}</span>
              <small className="contact-row-description">{t('settings.help.contactUsDescription')}</small>
            </span>
            <span className="contact-row-chevron" aria-hidden="true" />
          </Link>
        </div>

        <div className="version-info">{t('settings.version', { version: packageJson.version })}</div>
      </div>
    </div>
  );
}

export default Settings;
